import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Drag-resizable column widths, persisted per table.
 *
 * Reference §4: every table is resizable, widths persist to `localStorage`, and there is
 * **no adjacent-column compensation** — dragging a column changes only that column and the
 * table's total width. Compensation makes a drag feel like it fights back.
 */
const MIN_WIDTH = 56;
/*
  The floor under the drag ceiling, not the ceiling itself.

  A column may be dragged as wide as the scroll container it sits in — "consume the width
  available" — and this is only what that ceiling can never fall below, so the behaviour on
  a narrow viewport is no worse than it was.
*/
const MIN_MAX_WIDTH = 900;

/** Column keys are identifiers, so this can never appear inside one. */
const SEPARATOR = '|';

function storageKey(key: string): string {
  return `table.${key}.colWidths`;
}

export function useColumnWidths(key: string, columns: readonly { key: string; width: number }[]) {
  /*
    The key list and the default widths are tracked as signatures rather than as the array
    itself. Callers build that array inline, so its identity changes on every render while
    its contents almost never do — depending on it directly would rewrite storage
    continuously.
  */
  const keySignature = columns.map((column) => column.key).join(SEPARATOR);
  const widthSignature = columns.map((column) => column.width).join(SEPARATOR);

  /*
    Stored BY COLUMN KEY, never by position. Columns move — the shell alone pushes the
    status column to the end — and a positional layout would silently hand one column's
    dragged width to whichever column inherited its index.
  */
  const [widths, setWidths] = useState<number[]>(() => {
    const defaults = columns.map((column) => column.width);

    try {
      const stored = localStorage.getItem(storageKey(key));

      if (stored) {
        const parsed = JSON.parse(stored) as unknown;

        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const byKey = parsed as Record<string, unknown>;

          return columns.map((column, index) =>
            typeof byKey[column.key] === 'number' ? (byKey[column.key] as number) : defaults[index],
          );
        }
      }
    } catch {
      // Blocked or unparsable storage — fall through to the defaults. A layout written by
      // the older positional format simply fails the object check and is discarded.
    }

    return defaults;
  });

  useEffect(() => {
    try {
      const byKey: Record<string, number> = {};

      keySignature.split(SEPARATOR).forEach((columnKey, index) => {
        byKey[columnKey] = widths[index];
      });

      localStorage.setItem(storageKey(key), JSON.stringify(byKey));
    } catch {
      // Persisting the layout is a convenience, never a requirement.
    }
  }, [key, widths, keySignature]);

  const drag = useRef<{ index: number; startX: number; startWidth: number; maxWidth: number } | null>(null);

  const onPointerMove = useCallback((event: PointerEvent) => {
    const state = drag.current;
    if (!state) return;

    const next = Math.min(state.maxWidth, Math.max(MIN_WIDTH, state.startWidth + (event.clientX - state.startX)));

    setWidths((current) => {
      const copy = [...current];
      copy[state.index] = next;
      return copy;
    });
  }, []);

  const onPointerUp = useCallback(() => {
    drag.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  const startResize = useCallback((index: number, event: React.PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();

    /*
      Seed from the column's MEASURED width, not the stored one.

      A fixed-layout table stretches columns to fill the viewport, so the rendered width is
      usually wider than the stored value. Seeding from the stored number made the column
      jump backwards on the first pixel of movement instead of tracking the pointer.
    */
    const header = (event.currentTarget as HTMLElement).closest('th');
    const measured = header?.getBoundingClientRect().width;

    /*
      The ceiling is the scrolling panel the table lives in, measured now rather than fixed
      in advance: a column can be dragged out to fill the width on offer, and no further.
      A fixed 900px cap stopped a drag dead on a wide screen with a long value still cut off.
    */
    const scroller = header?.closest('.modern-scrollbar') ?? header?.offsetParent ?? null;
    const available = scroller instanceof HTMLElement ? scroller.clientWidth : window.innerWidth;

    drag.current = {
      index,
      startX: event.clientX,
      startWidth: Math.round(measured ?? MIN_WIDTH),
      maxWidth: Math.max(MIN_MAX_WIDTH, available),
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const reset = useCallback(
    () => setWidths(widthSignature.split(SEPARATOR).map(Number)),
    [widthSignature],
  );

  return { widths, startResize, reset };
}
